import React from "react";
import { Box, Typography, Button } from "@mui/material";

// helper: preview images, open non-images in a new tab
const isImg = (url = "") => /\.(png|jpe?g|webp|gif)$/i.test(url.split("?")[0]);
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const statusStyles = (status = "") => {
  const s = status.toLowerCase();
  if (s.includes("active"))
    return { bg: "#d1fae5", fg: "#065f46", label: "Active" };
  if (s.includes("expired"))
    return { bg: "#fee2e2", fg: "#991b1b", label: "Expired" };
  if (s.includes("suspend"))
    return { bg: "#fef3c7", fg: "#7c2d12", label: "Suspended" };
  return { bg: "#f3f4f6", fg: "#374151", label: status || "Under Review" };
};

const AgencyLicenseCard = ({ license, onEdit, onDelete }) => {
  // map backend fields -> UI
  const {
    license_type, // e.g. "Brokerage", "Business"
    license_number, // string
    issuing_state, // e.g. "FL"
    issued_date, // ISO
    expiry_date, // ISO
    status, // "Active" | "Expired" | ...
    license_document_url, // may be image or pdf
    verified_at, // null or timestamp
  } = license || {};

  const isVerified = !!verified_at;
  const st = statusStyles(status);

  return (
    <Box
      sx={{
        border: "1px solid #ddd",
        borderRadius: 2,
        p: 2,
        maxWidth: 500,
        width: "100%",
        margin: "0 auto",
        backgroundColor: "#fff",
        boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header row */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="h6">
          {license_type ? `${license_type} License` : "Agency License"}
        </Typography>

        <Box
          sx={{
            px: 1.25,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: st.bg,
            color: st.fg,
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          {st.label}
        </Box>

        {/* verification badge */}
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: isVerified ? "#d1fae5" : "#fef2f2",
            color: isVerified ? "#065f46" : "#991b1b",
            fontSize: "0.75rem",
            fontWeight: 600,
            mr: 1,
          }}
        >
          {isVerified ? "Verified" : "Under Review"}
        </Box>

        {/* status pill */}
      </Box>

      {/* Meta */}
      <Typography variant="body1">
        <strong>Issuing State:</strong> {issuing_state || "—"}
      </Typography>

      <Typography variant="body1">
        <strong>License #:</strong> {license_number || "—"}
      </Typography>

      <Typography variant="body1">
        <strong>Start (Issued):</strong> {fmt(issued_date)}
      </Typography>

      <Typography variant="body1" sx={{ mb: 2 }}>
        <strong>Expires:</strong> {fmt(expiry_date)}
      </Typography>

      {/* Document preview / link */}
      {license_document_url ? (
        isImg(license_document_url) ? (
          <img
            src={license_document_url}
            alt="License"
            style={{
              height: "140px",
              width: "100%",
              objectFit: "cover",
              borderRadius: "8px",
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
              marginBottom: "16px",
            }}
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        ) : (
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              window.open(license_document_url, "_blank", "noopener")
            }
            sx={{ mb: 2 }}
          >
            View Document
          </Button>
        )
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 2 }}
        >
          No document uploaded
        </Typography>
      )}

      {/* Actions (optional) */}
      {(onEdit || onDelete) && (
        <Box display="flex" justifyContent="flex-end" gap={1}>
          {onEdit && (
            <Button onClick={() => onEdit(license)} variant="outlined">
              Edit
            </Button>
          )}
          {onDelete && (
            <Button
              onClick={() => onDelete(license.id)}
              color="error"
              variant="outlined"
            >
              Delete
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AgencyLicenseCard;
